import React, { useCallback, useRef, useImperativeHandle } from 'react';
import { HiOutlineVideoCamera } from 'react-icons/hi';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

export interface ChatVideoUploadProps {
  reachMaxFiles: boolean;
  onUpload: (file: File) => void;
}

export interface ChatVideoUploadHandle {
  open: () => void;
}

export const ChatVideoUpload = React.forwardRef<
  ChatVideoUploadHandle,
  ChatVideoUploadProps
>(({ reachMaxFiles, onUpload }, ref) => {
  const isVideoUploadEnabled = useFeatureBit(FeatureBit.AI_UPLOAD_VIDEO);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const onVideoClick = useCallback(() => {
    videoInputRef.current?.click();
  }, []);

  const onVideoFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files) {
        Array.from(files).forEach((file) => onUpload(file));
      }
      e.target.value = ``;
    },
    [onUpload],
  );

  useImperativeHandle(ref, () => ({
    open: onVideoClick,
  }));

  if (!isVideoUploadEnabled) {
    return null;
  }

  return (
    <>
      <HiOutlineVideoCamera
        size={19}
        onClick={onVideoClick}
        style={{
          pointerEvents: reachMaxFiles ? `none` : `auto`,
          opacity: reachMaxFiles ? 0.5 : 1,
        }}
      />
      <input
        type="file"
        accept="video/*"
        ref={videoInputRef}
        style={{ display: `none` }}
        onChange={onVideoFileChange}
      />
    </>
  );
});
